import type {
  StoryResponse,
  StoryType,
  MatchResponse,
  MatchConfig,
  DebugStoryResult,
  StatsResponse,
  AdminStoryDetail,
  AdminStoryListEntry,
  AdminMatchDetail,
  AdminMatchListEntry,
  AdminPebbleEnvelope,
  EmbedStatus,
  RefinementSession,
  RefinementSessionDetail,
  RefinementStatus,
  StoryRevision,
  NotifyLogEntry,
  ChatRequest,
  ChatResponse,
  MatchAnalysis,
} from './types';
import { useAuthStore } from '../stores/authStore';

// Use the vite dev proxy in dev (target: VITE_API_PROXY, see vite.config.ts);
// in a build the matcher origin is inlined from VITE_MATCHER_API_URL. Vite only
// exposes VITE_-prefixed vars to client code and inlines them at BUILD time, so
// a test build is:
//   VITE_MATCHER_API_URL=https://pebbl-matcher-test.green-dream-1015.workers.dev npm run build
// The fallback is the prod worker, so an unconfigured build behaves exactly as
// it did before this var existed. `||` (not `??`) so an empty string also falls
// back rather than producing relative-URL fetches.
const API_BASE = import.meta.env.DEV
  ? '/api'
  : (import.meta.env.VITE_MATCHER_API_URL ||
     'https://pebbl-matcher.green-dream-1015.workers.dev');

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function fetchApi<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const token = useAuthStore.getState().token;
  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  });

  if (res.status === 401) {
    // Only the admin auth middleware returns 401 — clear and re-prompt.
    useAuthStore.getState().clearToken(true);
    throw new ApiError('Unauthorized: admin token rejected', 401);
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(error.error || error.message || `API error: ${res.status}`, res.status);
  }

  return res.json();
}

// ==================== Stories ====================

export async function listStories(limit = 100, offset = 0) {
  return fetchApi<{
    stories: AdminStoryListEntry[];
    pagination: { limit: number; offset: number; count: number };
  }>(`/admin/stories?limit=${limit}&offset=${offset}`);
}

export async function getStoryDetail(id: string) {
  return fetchApi<AdminStoryDetail>(`/admin/stories/${id}`);
}

export async function getStoryWithMatches(id: string) {
  return fetchApi<{ story: StoryResponse; matches: MatchResponse[] }>(
    `/stories/${id}`
  );
}

export async function deleteStory(id: string) {
  return fetchApi<{ success: boolean; deleted: string }>(
    `/admin/stories/${id}`,
    { method: 'DELETE' }
  );
}

export async function reembedStory(id: string) {
  return fetchApi<{ success: boolean; storyId: string; matchesFound: number; totalLatencyMs: number }>(
    `/admin/stories/${id}/reembed`,
    { method: 'POST' }
  );
}

// ==================== Embed Status (07-DO-ALARM) ====================

/**
 * Live embed-status read for a story. Same shape as the `embed_status`
 * field embedded in `getStoryDetail`. Used after Cancel / Re-embed actions
 * to refresh the panel without a full page reload.
 */
export async function getEmbedStatus(id: string) {
  return fetchApi<EmbedStatus>(`/admin/stories/${id}/embed-status`);
}

/**
 * Cancel any pending embed-freshness retry alarm for a story. Idempotent.
 * Also clears `stories.embed_failure_reason` so a stuck/failed story can
 * be retried by an operator without leaving a stale failure marker.
 */
export async function cancelEmbedRetries(id: string) {
  return fetchApi<{ cancelled: boolean; storyId: string }>(
    `/admin/stories/${id}/embed-cancel`,
    { method: 'POST' }
  );
}

// ==================== Matches ====================

export async function getMatch(id: string) {
  return fetchApi<MatchResponse>(`/matches/${id}`);
}

export async function getAdminMatch(id: string) {
  return fetchApi<AdminMatchDetail>(`/admin/matches/${id}`);
}

export async function listMatches(params: { status?: string; flags?: string; limit?: number; offset?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  // Comma-separated flag ids; backend matches ANY-of. The matches page filters
  // client-side — this param exists for deep links / analyst tooling parity.
  if (params.flags) qs.set('flags', params.flags);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi<{
    matches: AdminMatchListEntry[];
    pagination: { limit: number; offset: number; count: number };
  }>(`/admin/matches${query}`);
}

// ==================== Refinement Sessions ====================

export interface ListRefinementSessionsParams {
  storyId?: string;
  pebbleId?: string;
  matchId?: string;
  status?: RefinementStatus;
  limit?: number;
  offset?: number;
}

export async function listRefinementSessions(params: ListRefinementSessionsParams = {}) {
  const qs = new URLSearchParams();
  if (params.storyId) qs.set('storyId', params.storyId);
  if (params.pebbleId) qs.set('pebbleId', params.pebbleId);
  if (params.matchId) qs.set('matchId', params.matchId);
  if (params.status) qs.set('status', params.status);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const query = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi<{
    sessions: RefinementSession[];
    pagination: { limit: number; offset: number; count: number };
  }>(`/admin/refinement_sessions${query}`);
}

export async function getRefinementSession(id: string) {
  return fetchApi<RefinementSessionDetail>(`/admin/refinement_sessions/${id}`);
}

export async function recomputeRefinementSession(id: string) {
  return fetchApi<{ success: boolean; session: RefinementSession | null }>(
    `/admin/refinement_sessions/${id}/recompute`,
    { method: 'POST' }
  );
}

export async function failRefinementSession(id: string) {
  return fetchApi<{ success: boolean; session: RefinementSession | null }>(
    `/admin/refinement_sessions/${id}/fail`,
    { method: 'POST' }
  );
}

export async function escalateRefinementSession(id: string) {
  return fetchApi<{ success: boolean; session: RefinementSession | null }>(
    `/admin/refinement_sessions/${id}/escalate`,
    { method: 'POST' }
  );
}

export async function cancelRefinementSession(id: string) {
  return fetchApi<{ success: boolean; session: RefinementSession | null }>(
    `/admin/refinement_sessions/${id}/cancel`,
    { method: 'POST' }
  );
}

// ==================== Story Revisions ====================

export async function listStoryRevisions(storyId: string) {
  return fetchApi<{ revisions: StoryRevision[] }>(
    `/admin/story_revisions?storyId=${encodeURIComponent(storyId)}`
  );
}

// ==================== Notify Log (doorbells) ====================

export async function listNotifyLog(pebbleId?: string, limit = 50) {
  const qs = new URLSearchParams();
  if (pebbleId) qs.set('pebbleId', pebbleId);
  qs.set('limit', String(limit));
  return fetchApi<{ entries: NotifyLogEntry[] }>(
    `/admin/notify_log?${qs.toString()}`
  );
}

export async function replayNotify(id: string) {
  return fetchApi<{ success: boolean; entry: NotifyLogEntry; error?: string }>(
    `/admin/notify_log/${id}/replay`,
    { method: 'POST' }
  );
}

// ==================== Pebble Envelope ====================

export async function getAdminPebbleEnvelope(pebbleId: string) {
  return fetchApi<AdminPebbleEnvelope>(`/admin/pebbles/${pebbleId}/envelope`);
}

// ==================== Debug (ConfigPlayground) ====================

export async function submitDebugStory(
  rawText: string,
  city: string,
  storyType: StoryType = 'stranger',
  config?: Partial<MatchConfig>,
  skipPersist = false
) {
  return fetchApi<DebugStoryResult>('/dev/debug/stories', {
    method: 'POST',
    body: JSON.stringify({ rawText, city, storyType, config, skipPersist }),
  });
}

// ==================== Config / Stats ====================

export async function getConfig() {
  return fetchApi<{ config: MatchConfig; description: Record<string, any> }>('/admin/config');
}

export async function getStats() {
  return fetchApi<StatsResponse>('/admin/stats');
}

// ==================== AI Analyst ====================

export async function postAdminChat(req: ChatRequest) {
  return fetchApi<ChatResponse>('/admin/ai/chat', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function postAdminAnalyze(matchId: string, force = false) {
  return fetchApi<MatchAnalysis>('/admin/ai/analyze', {
    method: 'POST',
    body: JSON.stringify({ matchId, force }),
  });
}

/** Cached-report probe; throws ApiError(status=404) when none exists for the current attempt. */
export async function getMatchAnalysis(matchId: string) {
  return fetchApi<MatchAnalysis>(`/admin/ai/analyses/${encodeURIComponent(matchId)}`);
}

// ==================== Test Harness (scenarios + run receipts) ====================

import type { TestScenario } from './types';

export async function listScenarios(type?: string) {
  const qs = type ? `?type=${encodeURIComponent(type)}` : '';
  return fetchApi<{ scenarios: TestScenario[] }>(`/admin/test/scenarios${qs}`);
}

export async function getScenario(id: string) {
  return fetchApi<TestScenario>(`/admin/test/scenarios/${id}`);
}

export async function createScenario(body: Partial<TestScenario>) {
  return fetchApi<TestScenario>('/admin/test/scenarios', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateScenario(id: string, body: Partial<TestScenario>) {
  return fetchApi<TestScenario>(`/admin/test/scenarios/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function deleteScenario(id: string) {
  return fetchApi<{ success: boolean; deleted: string }>(
    `/admin/test/scenarios/${id}`,
    { method: 'DELETE' },
  );
}

// ---- Run receipts (PD-5, migration 033) ----------------------------------

import type { ScenarioRun } from './types';

/**
 * `GET /admin/test/scenarios/:id/runs` — what the harness recorded for this
 * scenario, newest first.
 *
 * A database without migration 033 answers 503 `scenario_runs_unavailable`;
 * that arrives as an `ApiError` and the caller renders the reason, because the
 * alternative — an empty list — reads as "this scenario has never been run",
 * which is a different and much more misleading statement.
 */
export async function listScenarioRuns(scenarioId: string, limit = 20) {
  return fetchApi<{ runs: ScenarioRun[] }>(
    `/admin/test/scenarios/${encodeURIComponent(scenarioId)}/runs?limit=${limit}`,
  );
}

/** `GET /admin/test/runs` — the global tail, optionally one path. */
export async function listRuns(params: { scenarioId?: string; path?: string; limit?: number } = {}) {
  const qs = new URLSearchParams();
  if (params.scenarioId) qs.set('scenarioId', params.scenarioId);
  if (params.path) qs.set('path', params.path);
  qs.set('limit', String(params.limit ?? 50));
  return fetchApi<{ runs: ScenarioRun[] }>(`/admin/test/runs?${qs.toString()}`);
}

export async function getScenarioRun(runId: string) {
  return fetchApi<ScenarioRun>(`/admin/test/runs/${encodeURIComponent(runId)}`);
}

export async function deleteScenarioRun(runId: string) {
  return fetchApi<{ success: boolean; deleted: string }>(
    `/admin/test/runs/${encodeURIComponent(runId)}`,
    { method: 'DELETE' },
  );
}

// ==================== extracted_v1 parity (PB-6 / PD-5) ====================

import type { ExtractedParityReport } from './types';

/**
 * `GET /admin/extracted-parity` — the per-family agreement table §10.3 gates
 * the cutover on, and the raw rows behind it.
 *
 * `rows` defaults to 0 on the route (summary only); the drill-down asks for
 * them explicitly. Scoping by `pebbleId` is what gives a scenario RUN a parity
 * report of its own — the run's sides carry the pebble ids.
 */
export async function getExtractedParity(
  params: {
    family?: string;
    pebbleId?: string;
    requestKey?: string;
    windowDays?: number;
    sinceMs?: number;
    rows?: number;
  } = {},
): Promise<ExtractedParityReport> {
  const qs = new URLSearchParams();
  if (params.family) qs.set('family', params.family);
  if (params.pebbleId) qs.set('pebbleId', params.pebbleId);
  if (params.requestKey) qs.set('requestKey', params.requestKey);
  if (params.windowDays != null) qs.set('windowDays', String(params.windowDays));
  if (params.sinceMs != null) qs.set('sinceMs', String(params.sinceMs));
  if (params.rows != null) qs.set('rows', String(params.rows));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi<ExtractedParityReport>(`/admin/extracted-parity${suffix}`);
}

// ==================== Conversation trace (PD-1) ====================

import type { ConversationTrace, ConversationTraceOptions } from './types';

/**
 * `GET /admin/conversation/:pebbleId` — one pebble's whole trace in one request
 * (PD-1). The five stores it joins are described in
 * `docs/decisions/PD-1-admin-conversation-trace.md`.
 *
 * Params are omitted when unset so the route's own defaults apply; only an
 * explicit `0` (or `d1: false`) skips a source, which is how the timeline page
 * offers a cheap re-read on a huge pebble.
 *
 * `{ success, data }` is unwrapped here for the same reason every other reader
 * in this file unwraps it: a component that has to remember which endpoints
 * envelope their payload gets it wrong once and renders `undefined`.
 */
export async function getConversationTrace(
  pebbleId: string,
  opts: ConversationTraceOptions = {},
): Promise<ConversationTrace> {
  const qs = new URLSearchParams();
  for (const name of ['transcript', 'verdicts', 'chats', 'messages', 'events'] as const) {
    const value = opts[name];
    if (value !== undefined) qs.set(name, String(value));
  }
  if (opts.d1 === false) qs.set('d1', '0');
  const suffix = qs.toString() ? `?${qs}` : '';
  const res = await fetchApi<{ success: boolean; data: ConversationTrace }>(
    `/admin/conversation/${encodeURIComponent(pebbleId)}${suffix}`,
  );
  return res.data;
}

// ==================== Match events (PD-4) ====================

import type { MatchEventsLog } from '../components/matchevents/model';
import type { MatchEventReplayResult } from './types';

/**
 * `GET /admin/match-events` — the `/match_events` nodes for one pebble (or the
 * global tail) joined to the D1 ingest ledger, which is the only store that
 * knows which `story_id` a request produced.
 *
 * Unwraps `{success, data}` for the same reason `getConversationTrace` does.
 */
export async function listMatchEvents(pebbleId?: string | null, limit = 50): Promise<MatchEventsLog> {
  const qs = new URLSearchParams();
  if (pebbleId) qs.set('pebble_id', pebbleId);
  qs.set('limit', String(limit));
  const res = await fetchApi<{ success: boolean; data: MatchEventsLog }>(
    `/admin/match-events?${qs.toString()}`,
  );
  return res.data;
}

/**
 * `POST /admin/match-events/:requestKey/replay` — re-fire a stored request node
 * through the matcher's ingest, the door PB-9's sweep re-fires a stuck node
 * through. The ingest's ledger owns the idempotency; the reply carries its
 * status and body verbatim so the caller can say what happened rather than
 * guess.
 */
export async function replayMatchEvent(requestKey: string): Promise<MatchEventReplayResult> {
  return fetchApi<MatchEventReplayResult>(
    `/admin/match-events/${encodeURIComponent(requestKey)}/replay`,
    { method: 'POST' },
  );
}
